import React, { useEffect, useState, useRef, TouchEvent } from 'react';
import ReactDOM from 'react-dom';
import './BottomSheet.scss';

interface BottomSheetProps {
   isOpen: boolean;
   onClose: () => void;
   title?: string;
   children: React.ReactNode;
   maxHeight?: string;
   height?: string;
   disableBodyScroll?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
   isOpen,
   onClose,
   title,
   children,
   maxHeight = '85vh',
   height,
   disableBodyScroll = false,
}) => {
   const [mounted, setMounted] = useState(false);
   const [animate, setAnimate] = useState(false);
   const [translateY, setTranslateY] = useState(0);
   const [isDragging, setIsDragging] = useState(false);
   const sheetRef = useRef<HTMLDivElement>(null);
   const startYRef = useRef(0);
   const currentYRef = useRef(0);

   useEffect(() => {
      if (isOpen) {
         setMounted(true);
         document.body.style.overflow = 'hidden';
         const timer = setTimeout(() => setAnimate(true), 10);
         return () => clearTimeout(timer);
      } else {
         setAnimate(false);
         const timer = setTimeout(() => {
            setMounted(false);
            document.body.style.overflow = '';
         }, 300);
         return () => clearTimeout(timer);
      }
   }, [isOpen]);

   useEffect(() => {
      return () => {
         document.body.style.overflow = '';
      };
   }, []);

   const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
      startYRef.current = e.touches[0].clientY;
      currentYRef.current = e.touches[0].clientY;
      setIsDragging(true);
   };

   const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      currentYRef.current = e.touches[0].clientY;
      const deltaY = currentYRef.current - startYRef.current;
      
      if (deltaY > 0) {
         setTranslateY(deltaY);
      }
   };

   const handleTouchEnd = () => {
      setIsDragging(false);
      const deltaY = currentYRef.current - startYRef.current;
      const threshold = 120;

      if (deltaY > threshold) {
         onClose();
      } else {
         setTranslateY(0);
      }
   };

   if (!mounted) return null;

   const backdropClass = `bottom-sheet-backdrop ${animate ? 'show' : ''}`;
   const sheetClass = `bottom-sheet-content ${animate ? 'show' : ''} ${isDragging ? 'dragging' : ''}`;

   return ReactDOM.createPortal(
      <div className="bottom-sheet-portal">
         <div className={backdropClass} onClick={onClose} />
         <div
            ref={sheetRef}
            className={sheetClass}
            style={{
               height,
               maxHeight,
               transform: isDragging ? `translateY(${translateY}px)` : undefined,
            }}
         >
            <div
               className="bottom-sheet-header-wrapper"
               onTouchStart={handleTouchStart}
               onTouchMove={handleTouchMove}
               onTouchEnd={handleTouchEnd}
            >
               <div className="bottom-sheet-drag-handle">
                  <div className="drag-handle-pill" />
               </div>
               {title && (
                  <div className="bottom-sheet-title">
                     <h5 className="m-0 fw-bold">{title}</h5>
                     <button
                        type="button"
                        className="btn-close bottom-sheet-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                     />
                  </div>
               )}
            </div>
            <div className={`bottom-sheet-body ${disableBodyScroll ? 'overflow-hidden' : ''}`} style={disableBodyScroll ? { overflowY: 'hidden' } : {}}>
               {children}
            </div>
         </div>
      </div>,
      document.body
   );
};
